import React from 'react';
import { Switch } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BRAND } from '../../theme/brandTokens';

const COMPACT_SWITCH_WIDTH = 34;
const COMPACT_SWITCH_HEIGHT = 20;
const COMPACT_THUMB_SIZE = 16;
const COMPACT_PADDING = 2;
const COMPACT_CHECKED_TRANSLATE = COMPACT_SWITCH_WIDTH - COMPACT_THUMB_SIZE - (COMPACT_PADDING * 2);

const DEFAULT_SWITCH_WIDTH = 39;
const DEFAULT_SWITCH_HEIGHT = 22;
const DEFAULT_THUMB_SIZE = 18;
const DEFAULT_PADDING = 2;
const DEFAULT_CHECKED_TRANSLATE = DEFAULT_SWITCH_WIDTH - DEFAULT_THUMB_SIZE - (DEFAULT_PADDING * 2);

const SWITCH_COLOR_SCHEMES = {
  default: {
    checkedTrack: '#4caf50',
    uncheckedTrack: '#d0d0d0',
  },
  brand: {
    checkedTrack: BRAND.accent,
    uncheckedTrack: '#d0d0d0',
  },
};

const StyledSwitchRoot = styled(Switch, {
  shouldForwardProp: (prop) => prop !== 'compact' && prop !== 'colorScheme',
})(({ theme, compact, colorScheme }) => {
  const switchWidth = compact ? COMPACT_SWITCH_WIDTH : DEFAULT_SWITCH_WIDTH;
  const switchHeight = compact ? COMPACT_SWITCH_HEIGHT : DEFAULT_SWITCH_HEIGHT;
  const thumbSize = compact ? COMPACT_THUMB_SIZE : DEFAULT_THUMB_SIZE;
  const padding = compact ? COMPACT_PADDING : DEFAULT_PADDING;
  const checkedTranslate = compact ? COMPACT_CHECKED_TRANSLATE : DEFAULT_CHECKED_TRANSLATE;
  const trackRadius = switchHeight / 2;
  const colors = SWITCH_COLOR_SCHEMES[colorScheme] || SWITCH_COLOR_SCHEMES.default;

  return {
    width: switchWidth,
    height: switchHeight,
    padding: 0,
    position: 'relative',
    display: 'inline-flex',
    verticalAlign: 'middle',
    overflow: 'hidden',
    '& .MuiSwitch-switchBase': {
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: 1,
      margin: 0,
      padding,
      width: switchWidth,
      height: switchHeight,
      transform: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      cursor: 'pointer',
      '&.Mui-checked': {
        transform: 'none',
        color: '#ffffff',
        '& .MuiSwitch-thumb': {
          transform: `translateX(${checkedTranslate}px)`,
        },
        '& + .MuiSwitch-track': {
          backgroundColor: colors.checkedTrack,
          opacity: 1,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        boxShadow: `0 0 0 2px ${BRAND.accent}`,
      },
      '&.Mui-disabled': {
        cursor: 'default',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        opacity: 0.8,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
      '& .MuiSwitch-input': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        margin: 0,
        padding: 0,
        cursor: 'inherit',
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: thumbSize,
      height: thumbSize,
      backgroundColor: '#ffffff',
      boxShadow: compact ? '0 1px 4px rgba(0, 0, 0, 0.2)' : '0 2px 7px rgba(0, 0, 0, 0.2)',
      transition: theme.transitions.create(['transform'], {
        duration: 250,
      }),
    },
    '& .MuiSwitch-track': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: switchWidth,
      height: switchHeight,
      borderRadius: trackRadius,
      backgroundColor: colors.uncheckedTrack,
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 250,
      }),
    },
  };
});

const StyledSwitch = ({
  compact = false,
  colorScheme = 'default',
  ...props
}) => (
  <StyledSwitchRoot
    compact={compact}
    colorScheme={colorScheme}
    disableRipple
    {...props}
  />
);

export default StyledSwitch;
